/**
 * Shared business logic for Stage Selector
 * Currently being used from in-app stage selector and workviews cell editor
 */
import { useMemo } from 'react'

import chroma from 'chroma-js'

import { useAlertsState } from '@/components/Alertsv2/useAlertState'
import { docExpiredAlertMessages } from '@/constants/alert'
import { userGroups } from '@/constants/groups'
import { useCurrentApplication } from '@/graphql/services/application'
import { useGetApplicationValuationsCalcsQuery } from '@/graphql/services/applicationCalcs'
import { useGetChecklistGroupsVisibleItemsQuery } from '@/services/v2-application/checklistGroup'
import {
  applicationStates,
  applicationStatesValues,
  managerGroups,
} from '@/utils/checklistGroupMappings'
import {
  isHSRUser,
  isHeoManager,
  isLockOverride,
  isProcessingUser,
  isSalesUser,
  isSuperUser,
} from '@/utils/user'

const ENABLED_STATES_GROUP = {
  [userGroups.SALES]: [
    applicationStatesValues.QUALIFICATION,
    applicationStatesValues.APP_SUBMIT,
    applicationStatesValues.DOC_UPLOAD,
    applicationStatesValues.FILE_SETUP,
    applicationStatesValues.COND_APPROVAL,
    applicationStatesValues.IE_INTENT_TO_PROCEED,
    applicationStatesValues.ORDER_OUT,
    applicationStatesValues.ICS_SIGNATURE,
    applicationStatesValues.DOC_DRAW,
    applicationStatesValues.CLOSED_LOST,
  ],
  [userGroups.HOMEOWNER_SUPPORT_REP]: [
    applicationStatesValues.QUALIFICATION,
    applicationStatesValues.APP_SUBMIT,
    applicationStatesValues.DOC_UPLOAD,
    applicationStatesValues.FILE_SETUP,
    applicationStatesValues.COND_APPROVAL,
    applicationStatesValues.IE_INTENT_TO_PROCEED,
    applicationStatesValues.ORDER_OUT,
    applicationStatesValues.ICS_SIGNATURE,
    applicationStatesValues.DOC_DRAW,
    applicationStatesValues.CLOSED_LOST,
  ],
  [userGroups.PROCESSING]: [
    applicationStatesValues.FILE_SETUP,
    applicationStatesValues.COND_APPROVAL,
    applicationStatesValues.IE_DELIVERY,
    applicationStatesValues.IE_INTENT_TO_PROCEED,
    applicationStatesValues.ORDER_OUT,
    applicationStatesValues.THIRD_RD_PARTY_RECEIPTS,
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.FINAL_APPROVAL_UAM,
    applicationStatesValues.ICS_SIGNATURE,
  ],
  [userGroups.VALUATIONS]: [
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.FINAL_APPROVAL_UAM,
  ],
  [userGroups.UNDERWRITING]: [
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.FINAL_APPROVAL_UAM,
    applicationStatesValues.ICS_DELIVERY,
  ],
  [userGroups.UAM_REVIEW]: [
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.FINAL_APPROVAL_UAM,
    applicationStatesValues.ICS_DELIVERY,
  ],
  [userGroups.UW_REVIEW]: [
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.FINAL_APPROVAL_UAM,
    applicationStatesValues.ICS_DELIVERY,
  ],
  [userGroups.CLOSING]: [
    applicationStatesValues.ICS_SIGNATURE,
    applicationStatesValues.DOC_DRAW,
    applicationStatesValues.SIGNATURE,
    applicationStatesValues.ESCROW_DISBURSEMENT,
    applicationStatesValues.RECORDING,
    applicationStatesValues.CLOSED_WON,
  ],
  [userGroups.POST_CLOSING]: [
    applicationStatesValues.ICS_SIGNATURE,
    applicationStatesValues.DOC_DRAW,
    applicationStatesValues.SIGNATURE,
    applicationStatesValues.ESCROW_DISBURSEMENT,
    applicationStatesValues.RECORDING,
    applicationStatesValues.CLOSED_WON,
  ],
  [userGroups.SERVICING]: [
    applicationStatesValues.QUALIFICATION,
    applicationStatesValues.APP_SUBMIT,
  ],
  [managerGroups.HEO_MANAGER]: [
    applicationStatesValues.QUALIFICATION,
    applicationStatesValues.APP_SUBMIT,
    applicationStatesValues.DOC_UPLOAD,
    applicationStatesValues.FILE_SETUP,
    applicationStatesValues.COND_APPROVAL,
    applicationStatesValues.IE_DELIVERY,
    applicationStatesValues.IE_INTENT_TO_PROCEED,
    applicationStatesValues.ORDER_OUT,
    applicationStatesValues.THIRD_RD_PARTY_RECEIPTS,
    applicationStatesValues.FINAL_APPROVAL_UW,
    applicationStatesValues.ICS_SIGNATURE,
    applicationStatesValues.DOC_DRAW,
    applicationStatesValues.CLOSED_LOST,
  ],
  // TODO: Pending to add TS manager
}

export const isUserInGroup = (user, groupName) => {
  return user?.groups?.some((group) => group.name === groupName)
}

export const isStateDisabledForGroup = (state, user) => {
  // INFO: Returns are backwards because we want to disable the option, false means the group is enabled and true means the group is disabled
  for (const group in ENABLED_STATES_GROUP) {
    if (isUserInGroup(user, group)) {
      if (ENABLED_STATES_GROUP[group].includes(state?.application_state)) {
        return false
      }
    }
  }
  return true
}

const hasSpecialPermission = (user) => {
  return user?.is_superuser || isLockOverride(user)
}

const isDisabledFromClosedWon = (currentState, state) => {
  return (
    currentState === applicationStatesValues.CLOSED_WON &&
    state.application_state !== applicationStatesValues.CLOSED_WON
  )
}

export const isDisabledByNegativeNetCashProceeds = (state, alerts) => {
  return (
    state?.application_state === applicationStatesValues.FINAL_APPROVAL_UAM &&
    !!alerts?.find((alert) => alert?.message === 'bounds-negative-net-cash-proceeds')
  )
}

export const isDisabledByAppraisalReviewStatus = (state, { valuationsCalculations }) => {
  return (
    state?.application_state === applicationStatesValues.FINAL_APPROVAL_UAM &&
    valuationsCalculations?.appraisalReviewStatus !== 'Approved'
  )
}

export const isDisabledByExpiredDocsAlerts = (user, currentState, state, alerts) => {
  if (
    state.application_state === applicationStatesValues.CLOSED_LOST &&
    (isHeoManager(user) || isSalesUser(user) || isHSRUser(user))
  )
    return false

  return (
    alerts.some((alert) => docExpiredAlertMessages.includes(alert.message)) &&
    currentState !== state.application_state
  )
}

export const isDisabledByExistentAlerts = (user, currentState, alerts) => {
  if (isProcessingUser(user)) {
    return (
      alerts?.length > 0 &&
      currentState?.application_state === applicationStatesValues.FINAL_APPROVAL_UAM
    )
  }

  return false
}

const isDisabledByEscalations = (hasEscalationsNotApproved, state) => {
  return (
    hasEscalationsNotApproved &&
    state.index > 3 &&
    state.application_state !== applicationStatesValues.CLOSED_LOST
  )
}

const isDisabledByTasks = (blockedStateIndex, state) => {
  return (
    blockedStateIndex &&
    state.index > blockedStateIndex &&
    state.application_state !== applicationStatesValues.CLOSED_LOST
  )
}

export const isDisabledOption = (
  state,
  currentState,
  user,
  isLoadingUser,
  blockedStateIndex,
  hasEscalationsNotApproved,
  alerts,
  application
) => {
  if (isLoadingUser) return true

  if (hasSpecialPermission(user)) return false

  return [
    isDisabledFromClosedWon(currentState, state),
    isDisabledByNegativeNetCashProceeds(state, alerts),
    isDisabledByAppraisalReviewStatus(state, application),
    isDisabledByExpiredDocsAlerts(user, currentState, state, alerts),
    isDisabledByExistentAlerts(user, state, alerts),
    isDisabledByEscalations(hasEscalationsNotApproved, state),
    isDisabledByTasks(blockedStateIndex, state),
    isStateDisabledForGroup(state, user),
  ].some(Boolean)
}

export const customStyles = {
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.color)
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? data.color
          : isFocused
            ? color.alpha(0.1).css()
            : undefined,
      color: isDisabled
        ? '#ccc'
        : isSelected
          ? chroma.contrast(color, 'white') > 2
            ? 'white'
            : 'black'
          : data.color,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor: !isDisabled
          ? isSelected
            ? data.color
            : color.alpha(0.3).css()
          : undefined,
      },
    }
  },
  control: (provided) => ({
    ...provided,
    border: 'none',
    minWidth: 200,
    minHeight: 0,
    padding: 0,
    boxShadow: 'none',
  }),
  dropDown: (provided) => ({
    ...provided,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  input: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  // Add "Pending" to selected options that aren't "Closed"
  singleValue: (provided, state) => ({
    ...provided,
    '&:before': {
      content: `"${!state.data.value.startsWith('close') ? 'Pending ' : ''}"`,
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 50,
  }),
}

export const shouldOpenKickBackModal = (option, user, actualState) => {
  if (
    (user?.is_superuser ||
      user.groups.find((group) => group.name == userGroups.PROCESSING) ||
      user.groups.find((group) => group.name == userGroups.UNDERWRITING)) &&
    actualState === applicationStatesValues.COND_APPROVAL &&
    option.value === applicationStatesValues.FILE_SETUP
  )
    return true
  // TODO: Add UW Review permission when we have it
  else if (
    (user?.is_superuser ||
      user.groups.find(
        (group) =>
          group.name == userGroups.UAM_REVIEW ||
          group.name == userGroups.UW_REVIEW ||
          group.name == userGroups.UNDERWRITING
      )) &&
    actualState === applicationStatesValues.FINAL_APPROVAL_UAM &&
    option.value === applicationStatesValues.FINAL_APPROVAL_UW
  )
    return true
  else if (
    user.groups.find((group) => group.name == userGroups.CLOSING) &&
    actualState === applicationStatesValues.DOC_DRAW &&
    option.value === applicationStatesValues.ICS_SIGNATURE
  )
    return true
  return false
}

export const calcIsSelectDisabled = (user, application, actualState) => {
  const actualDate = new Date()
  const applicationStateModifiedAt = new Date(application.application_state_modified_at)
  const deltaTime = actualDate.getTime() - applicationStateModifiedAt.getTime()
  const tenDays = 10 * 24 * 60 * 60 * 1000
  return (
    deltaTime > tenDays &&
    actualState === applicationStatesValues.CLOSED_LOST &&
    !isHeoManager(user) &&
    !user?.is_superuser
  )
}

export const useGenerateOptions = (
  applicationUUID,
  currentState,
  currentStateIndex,
  user,
  isLoadingUser: boolean,
  hasEscalationsNotApproved: boolean
) => {
  const { alerts } = useAlertsState()
  const { data: application } = useCurrentApplication()
  const { data: checklistGroups } = useGetChecklistGroupsVisibleItemsQuery(applicationUUID, {
    skip: !applicationUUID,
  })

  const { data: applicationValuationsCalcsResponse } = useGetApplicationValuationsCalcsQuery(
    { externalId: application?.externalId },
    { skip: !application?.externalId }
  )

  const valuationsCalculations =
    applicationValuationsCalcsResponse?.application?.valuationsCalculations

  const blockedStateIndex = useMemo(() => {
    if (currentStateIndex === undefined || !checklistGroups?.results) return 0
    return (
      applicationStates
        .slice(currentStateIndex)
        .find((state) =>
          checklistGroups.results.some(
            (group) =>
              group?.type_name === state.type_name &&
              group?.checklist_items?.some((item) => item.status !== 'done' && !item.deleted_at)
          )
        )?.index ?? 0
    )
  }, [checklistGroups, currentStateIndex])

  return applicationStates.map((state) => ({
    value: state.application_state,
    label: state.type_name,
    color: state.color,
    index: state.index,
    disabled: isDisabledOption(
      state,
      currentState,
      user,
      isLoadingUser,
      blockedStateIndex,
      hasEscalationsNotApproved,
      alerts,
      { valuationsCalculations }
    ),
  }))
}
